import React, { Suspense, useEffect, useRef } from 'react';
import { Button, Skeleton } from '@amzn/storm-ui';
import { angleDown } from '@amzn/storm-ui-icons';
import { CountrySwitcherPublisher } from 'src/commons/utils/metrics';
import useAccountInfo from 'src/commons/hooks/useAccountInfo';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import { useHeaderBundle } from 'src/providers/PantherLocalizationProvider';

export interface MarketplaceSwitcherProps {
  pathOverride?: string;
}

const MarketplaceSwitcherInner = React.lazy(
  () =>
    import(
      /* webpackChunkName: "marketplace-switcher" */ './MarketplaceSwitcherInner'
    ),
);

const MarketplaceSwitcherPlaceholder: React.VFC = () => {
  const bundle = useHeaderBundle();
  const label = bundle.getMessage('country-selector-header');
  return (
    <Skeleton
      label={label}
      variant="rectangular"
      inline
      data-testid="aac-marketplace-switcher-placeholder"
    >
      <Button disabled iconEnd={angleDown}>
        {label}
      </Button>
    </Skeleton>
  );
};

const MarketplaceSwitcher: React.VFC<MarketplaceSwitcherProps> = ({
  pathOverride = '',
}) => {
  const {
    countryCodes,
    countryCodeToMarketplaceMap,
    canManageAccounts,
    currentAccount,
    globalAccountId,
    isLoading,
    selectedCountryCode,
  } = useAccountInfo();

  const handleSelect = (selectedCode: string) => {
    const marketplace = countryCodeToMarketplaceMap.get(selectedCode);
    if (marketplace?.url) {
      const redirectUrl = new URL(`https://${marketplace.url}`);

      if (pathOverride !== '') {
        redirectUrl.pathname = pathOverride;
      }

      CountrySwitcherPublisher.publishCounterMonitor('switched', 1);
      window.location.assign(redirectUrl.toString());
    }
  };

  const isVisible =
    currentAccount?.entityId !== undefined &&
    !(Array.isArray(countryCodes) && countryCodes.length === 0);

  // `rendered` counts page views that showed the switcher, so it must fire
  // once per mount, not once per React render: publishing from the render
  // body over-counted ~2.8x per page load. It fires before the lazy chunk
  // arrives, so "on screen" is `rendered` minus MarketplaceSwitcherChunk-Error.
  const hasPublishedRendered = useRef(false);
  useEffect(() => {
    if (isVisible && !hasPublishedRendered.current) {
      hasPublishedRendered.current = true;
      CountrySwitcherPublisher.publishCounterMonitor('rendered', 1);
    }
  }, [isVisible]);

  const placeholder = <MarketplaceSwitcherPlaceholder />;

  /*
   * The boundary is rendered unconditionally and the visibility decision is
   * handed to the lazy child, so the chunk request overlaps the
   * `useAccountInfo` fetch. Gating the boundary on `isVisible` instead would
   * serialise the two, and would move the `rendered` counter behind the chunk.
   */
  return (
    <ErrorBoundary name="MarketplaceSwitcherChunk">
      <Suspense fallback={isLoading || isVisible ? placeholder : null}>
        <MarketplaceSwitcherInner
          isLoading={isLoading}
          placeholder={placeholder}
          isVisible={isVisible}
          countries={countryCodes}
          selected={selectedCountryCode}
          canManageAccounts={!!canManageAccounts}
          globalAccountId={globalAccountId}
          onSelect={handleSelect}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

export default MarketplaceSwitcher;
